import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocStepIndicator,
  DocCodeBlock,
  DocNavFooter,
} from "@/components/docs";
import { docsColors } from "@/lib/docs-theme";

export default function MsTeamsIntegrationPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Notification Methods", href: "/docs/notifications" },
    { label: "Microsoft Teams" },
  ];

  return (
    <DocLayout
      title="Microsoft Teams Integration - Uptime Monitor Documentation"
      description="Get instant Microsoft Teams notifications for monitor alerts. Adaptive Card format with clickable links and action buttons."
      canonical="https://uptime-monitor.io/docs/notifications/ms-teams"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Microsoft Teams Integration"
        description="Send monitor alerts to Microsoft Teams channels using Workflow webhooks. Get notified when monitors go down or recover."
      />

      {/* Overview */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>How It Works</h2>
          <p className={`${docsColors.text.body} mb-4`}>
            Uptime Monitor sends alerts to Microsoft Teams using Workflow webhooks. Messages use
            Adaptive Card format with a title, detail text, and action buttons.
          </p>
          <ul className={`space-y-2 ${docsColors.text.body}`}>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Monitor name appears as a clickable button linking to the dashboard
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Down alerts show the error reason
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Recovery alerts include total downtime
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* Webhook Setup */}
      <DocSection title="Setup Instructions">
        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 1: Create Workflow Webhook in Teams
          </h3>
          <DocCard>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Open the Teams channel where you want alerts
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click the <strong className={docsColors.text.heading}>+</strong> icon or go to
                      channel settings
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Search for <strong className={docsColors.text.heading}>Workflows</strong> and
                      select it
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Choose{" "}
                      <strong className={docsColors.text.heading}>
                        Post to a channel when a webhook request is received
                      </strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={5} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Give the workflow a name (e.g., &quot;Uptime Alerts&quot;)
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={6} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Copy the <strong className={docsColors.text.heading}>webhook URL</strong>
                    </p>
                  </div>
                </div>
              </li>
            </ol>
          </DocCard>
        </div>

        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 2: Configure in Uptime Monitor
          </h3>
          <DocCard>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      In Uptime Monitor, go to{" "}
                      <strong className={docsColors.text.heading}>Settings → Contacts</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click <strong className={docsColors.text.heading}>Add Contact</strong> and
                      select <strong className={docsColors.text.heading}>Microsoft Teams</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Enter a name (e.g., &quot;Production Alerts&quot;) and paste your webhook URL
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>Save the contact</p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={5} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Add the Microsoft Teams contact to your monitors
                    </p>
                  </div>
                </div>
              </li>
            </ol>
          </DocCard>
        </div>
      </DocSection>

      {/* Message Format */}
      <DocSection title="Message Format">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            Messages are sent as Adaptive Cards with a title, detail text, and an action button:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Down Alert</h3>
              <DocCodeBlock className="border border-slate-700">
                <div className={`${docsColors.text.body}`}>
                  <p className="font-bold text-red-600 dark:text-red-400 mb-1">
                    Production API is DOWN
                  </p>
                  <p className="mb-2">Connection timeout after 10000ms.</p>
                  <p className="text-blue-600 dark:text-blue-400">[View Monitor]</p>
                </div>
              </DocCodeBlock>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Recovery Alert</h3>
              <DocCodeBlock className="border border-slate-700">
                <div className={`${docsColors.text.body}`}>
                  <p className="font-bold text-green-600 dark:text-green-400 mb-1">
                    Production API is UP!
                  </p>
                  <p className="mb-2">It was down for 5 minutes.</p>
                  <p className="text-blue-600 dark:text-blue-400">[View Monitor]</p>
                </div>
              </DocCodeBlock>
            </div>
          </div>

          <DocAlert variant="info" className="mt-4 p-3">
            <p className={`text-sm ${docsColors.text.body}`}>
              <strong className={docsColors.text.heading}>Note:</strong> Adaptive Cards include
              action buttons that link directly to the monitor details page in your dashboard.
            </p>
          </DocAlert>
        </DocCard>
      </DocSection>

      {/* Best Practices */}
      <DocSection title="Best Practices">
        <DocCard>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use dedicated alert channels</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Create separate Teams channels for different environments or severity levels
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Multiple contacts for redundancy
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Create multiple Teams contacts for different channels and assign to monitors
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Test webhook connectivity</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Verify webhooks are working by pausing and resuming a monitor
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Teams Workflow webhooks require HTTPS
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Ensure your webhook URL uses HTTPS, which is the default for Teams Workflow
                  webhooks
                </p>
              </div>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Troubleshooting */}
      <DocSection title="Troubleshooting">
        <div className="space-y-4">
          <DocAlert variant="warning" className="p-6">
            <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
              Common Issues
            </h3>
            <div className="space-y-4">
              <div>
                <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                  Messages not appearing
                </h4>
                <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-4`}>
                  <li>• Verify webhook URL is correct and complete</li>
                  <li>• Check channel permissions and workflow status</li>
                  <li>• Ensure Teams contact is assigned to monitors</li>
                </ul>
              </div>

              <div>
                <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>Webhook errors</h4>
                <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-4`}>
                  <li>• Regenerate the webhook URL in Teams Workflows</li>
                  <li>• Check Teams admin policies for webhook restrictions</li>
                  <li>• Ensure the webhook URL uses HTTPS</li>
                </ul>
              </div>

              <div>
                <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                  Office 365 Connectors deprecated
                </h4>
                <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-4`}>
                  <li>• Office 365 Connectors are deprecated; use Workflow webhooks instead</li>
                  <li>• If you were using an old connector URL, create a new Workflow webhook</li>
                </ul>
              </div>
            </div>
          </DocAlert>
        </div>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/notifications/discord", text: "Discord" }}
        next={{ href: "/docs/notifications/pagerduty", text: "PagerDuty" }}
      />
    </DocLayout>
  );
}
